import React, { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import CenteredLoader from "../../components/CenteredLoader";
import { GET_USER_PROFILE } from "src/api/graphql/graph";
import { useApolloClient, useLazyQuery } from "@apollo/client";
import withNotifications from "../../components/Notification/withNotifications";
import { bugsnagClient } from "../../config/bugsnag";
import { activeAccountId, openDefaultAccount } from "../../store/accountStore";
import { pickDefaultAccount } from "../../store/defaultAccount";

const MainCommonLayout: React.FC = () => {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const [fetchProfile, { data, loading }] = useLazyQuery(GET_USER_PROFILE);
  // With no account chosen in this browser, which one to open is settled
  // before any page asks for data: a page would otherwise load the own
  // account — empty for a teammate, so App Insights and My Apps send them to
  // add an app — and only then be reloaded into the team's.
  const [accountReady, setAccountReady] = useState(
    () => activeAccountId() !== null,
  );

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (accountReady) return;
    let cancelled = false;
    pickDefaultAccount(apolloClient)
      .then((id) => {
        if (cancelled) return;
        if (id) openDefaultAccount(id);
        else setAccountReady(true);
      })
      .catch((error) => {
        console.error("Error picking the default account:", error);
        if (!cancelled) setAccountReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accountReady, apolloClient]);

  useEffect(() => {
    if (data?.checkUserProfile) {
      const { isOnboarded, isConfirmed, email, name } = data.checkUserProfile;
      if (!isOnboarded || !isConfirmed) {
        navigate("/onboarding");
      }
      if (email || name) {
        bugsnagClient.setUser(
          email || "unknown",
          email || undefined,
          name || undefined,
        );
      }
    }
  }, [data, navigate]);

  if (loading || !accountReady) return <CenteredLoader />;

  return <Outlet />;
};

export default withNotifications(MainCommonLayout);
